import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';
import { BiPrinter, BiSpreadsheet, BiX } from 'react-icons/bi';
import { Loader2 } from 'src/components/common/icons';
import { IInvoiceGroup, ILRPersonnel, ICompanyLookup } from 'src/models/dlcLease/IInvoiceGroup';
import GroupLeasesModal from './GroupLeaseModal';
import InvoiceGroupNavigation from './InvoiceGroupNavigation';

// --- Sub-Modal: LR Personnel ---
const LRPersonnelModal: React.FC<{ isOpen: boolean; onClose: () => void; onSelect: (p: ILRPersonnel) => void }> = ({ isOpen, onClose, onSelect }) => {
    const { get, loading, response } = useFetch();
    const [personnel, setPersonnel] = useState<ILRPersonnel[]>([]);

    useEffect(() => {
        if (isOpen) {
            const load = async () => {
                const data = await get('/v1.0/leases/invoice-groups/personnel');
                if (response.ok) {
                    setPersonnel(data);
                }
            };
            load();
        }
    }, [isOpen, get, response]);

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.modalOverlay}>
            <div style={{ ...styles.modalContent, width: '500px', height: '600px', display: 'flex', flexDirection: 'column' }}>
                <div style={styles.modalHeader}>
                    <h3 style={styles.modalTitle}>Select Lease Records Personnel</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
                        <BiX size={24} />
                    </button>
                </div>
                <div style={{ padding: '1rem', flex: 1, overflowY: 'auto' }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                            <Loader2 style={{ animation: 'spin 1s linear infinite', width: 24, height: 24 }} />
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #E5E7EB', textAlign: 'left', backgroundColor: '#F9FAFB' }}>
                                    <th style={{ padding: '0.75rem' }}>Name</th>
                                    <th style={{ padding: '0.75rem' }}>Phone</th>
                                </tr>
                            </thead>
                            <tbody>
                                {personnel.map((p, i) => (
                                    <tr
                                        key={i}
                                        style={{ cursor: 'pointer', borderBottom: '1px solid #F3F4F6' }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F3F4F6')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                        onDoubleClick={() => onSelect(p)}
                                    >
                                        <td style={{ padding: '0.75rem' }}>{p.name1}</td>
                                        <td style={{ padding: '0.75rem' }}>{p.phone}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div style={{ padding: '1rem', borderTop: '1px solid #E5E7EB', fontSize: '0.8rem', color: '#6B7280', backgroundColor: '#F9FAFB' }}>
                    Double click a row to select.
                </div>
            </div>
        </div>
    );
};

// --- Main Modal ---
interface IProps {
    isOpen: boolean;
    onClose: () => void;
    data: IInvoiceGroup | null; // Data passed from parent
    companies: ICompanyLookup[]; // Lookups passed from parent
    onCreateClick?: () => void; // Omit when opened outside a lease context
    relatedGroups?: IInvoiceGroup[];
    currentGroupIndex?: number;
    onNavigate?: (index: number) => void;
    onDeleted?: (groupNo: number) => void;
}

export const getInvoiceGroupLprsDownload = (groupNo: number): { url: string; fileName: string } => ({
    url: `/v1.0/leases/invoice-groups/${groupNo}/print/lprs`,
    fileName: `InvoiceGroup_${groupNo}_LPRs.pdf`,
});

const InvoiceGroupModal: React.FC<IProps> = ({
    isOpen,
    onClose,
    data,
    companies,
    onCreateClick,
    relatedGroups = [],
    currentGroupIndex = 0,
    onNavigate,
    onDeleted,
}) => {
    // Local state for editing form fields
    const [group, setGroup] = useState<IInvoiceGroup | null>(null);
    const [showPersonModal, setShowPersonModal] = useState(false);
    const [showLeasesModal, setShowLeasesModal] = useState(false);
    // State for download loading indicators
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
    const [isDownloadingLprs, setIsDownloadingLprs] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);
    const { del, get, put, response, loading } = useFetch();

    // Sync local state when prop data changes
    useEffect(() => {
        setGroup(data);
        setRequestError(null);
    }, [data]);

    const handleChange = (field: keyof IInvoiceGroup, value: unknown) => {
        if (group) {
            setGroup({ ...group, [field]: value });
        }
    };

    const handlePersonSelect = (person: ILRPersonnel) => {
        if (group) {
            setGroup({ ...group, signName: person.name1, signPhone: person.phone });
        }
        setShowPersonModal(false);
    };

    const handleSave = async () => {
        if (!group?.groupNo || isSaving) {
            return;
        }

        setIsSaving(true);
        setRequestError(null);

        try {
            const updatedGroup = await put(`/v1.0/leases/invoice-groups/${group.groupNo}`, group);
            if (!response.ok) {
                setRequestError(parseLeaseRecordsApiError(updatedGroup || response.data, response.status));
                return;
            }

            setGroup(updatedGroup);
            onClose();
        } catch (error) {
            setRequestError(parseLeaseRecordsApiError(undefined, 500));
        } finally {
            setIsSaving(false);
        }
    };

    const downloadFile = async (url: string, fileName: string, setLoading: (l: boolean) => void) => {
        if (!group?.groupNo) {
            return;
        }

        setLoading(true);
        setRequestError(null);
        try {
            // 1. Make the request using useFetch
            await get(url);

            // 2. Check Success
            if (response.ok) {
                // 3. Get Blob
                const blob = await response.blob();

                // 4. Create Download Link
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = fileName; // Force download with specific name
                document.body.appendChild(link);

                // 5. Trigger Click & Cleanup
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);
            } else {
                setRequestError(parseLeaseRecordsApiError(response.data || { title: 'Failed to generate report.' }, response.status));
            }
        } catch (error) {
            // Download failed — error captured by catch block
            setRequestError(parseLeaseRecordsApiError({ title: 'Error downloading file.' }, 500));
        } finally {
            setLoading(false);
        }
    };

    const handlePrintPdf = () => {
        if (!group?.groupNo) {
            return;
        }
        const url = `/v1.0/leases/invoice-groups/${group.groupNo}/print/pdf`;
        const fileName = `InvoiceGroup_${group.groupNo}.pdf`;
        downloadFile(url, fileName, setIsDownloadingPdf);
    };

    const handleDownloadExcel = () => {
        if (!group?.groupNo) {
            return;
        }
        const url = `/v1.0/leases/invoice-groups/${group.groupNo}/print/excel`;
        const fileName = `InvoiceGroupList_${group.groupNo}.xlsx`;
        downloadFile(url, fileName, setIsDownloadingExcel);
    };

    const handlePrintLprs = () => {
        if (!group?.groupNo) {
            return;
        }
        const { url, fileName } = getInvoiceGroupLprsDownload(group.groupNo);
        downloadFile(url, fileName, setIsDownloadingLprs);
    };

    const handleRemove = async () => {
        if (!group?.groupNo) {
            return;
        }

        if (!window.confirm(`Are you sure you want to delete Invoice Group #${group.groupNo}?\n\nThis will detach all associated invoices.`)) {
            return;
        }

        await del(`/v1.0/leases/invoice-groups/${group.groupNo}`);

        if (response.ok) {
            alert('Group removed successfully.'); // Optional feedback
            setGroup(null); // Clear local data
            if (onDeleted) {
                onDeleted(group.groupNo);
            } else {
                onClose();
            }
        } else {
            // If the SP returns error status, it usually comes as BadRequest with message
            setRequestError(parseLeaseRecordsApiError(response.data, response.status));
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.modalOverlay}>
            <div style={{ ...styles.modalContent, width: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div style={styles.modalHeader}>
                    <h3 style={styles.modalTitle}>Invoice Group {group ? `- #${group.groupNo}` : ''}</h3>
                    {relatedGroups.length > 1 && onNavigate && (
                        <InvoiceGroupNavigation
                            groups={relatedGroups}
                            currentIndex={currentGroupIndex}
                            onNavigate={onNavigate}
                            buttonStyle={styles.buttonSecondary}
                        />
                    )}
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
                        <BiX size={24} />
                    </button>
                </div>

                <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />

                <div style={{ ...styles.gridBody, padding: '1.5rem', backgroundColor: '#F3F4F6' }}>
                    {!group ? (
                        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                            <p style={{ marginBottom: '1rem' }}>No Invoice Group record found associated with this lease.</p>
                            <p style={{ fontSize: '0.9rem' }}>Click &quot;Create New&quot; below to search for invoices and build a group.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {/* Row 1: Header Info */}
                            <div style={{ display: 'grid', gridTemplateColumns: '150px 150px 1fr', gap: '1rem' }}>
                                <Field label="Group No" value={group.groupNo} />
                                <Field label="Group Date" value={group.groupDate ? new Date(group.groupDate).toLocaleDateString() : ''} />
                                <div style={styles.fieldContainer}>
                                    <label style={styles.fieldLabel}>Company ID</label>
                                    <select
                                        style={styles.selectBase}
                                        value={group.compId || ''}
                                        onChange={(e) => handleChange('compId', e.target.value === '' ? undefined : Number(e.target.value))}
                                    >
                                        <option value="">Select Company...</option>
                                        {companies.map((c) => (
                                            <option key={c.compId} value={c.compId}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Row 2: Client No */}
                            <div style={{ width: '150px' }}>
                                <Field label="Client No." value={group.clientNo} />
                            </div>

                            {/* Row 3: Main Form Body */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '0.5rem' }}>
                                {/* Left Column: Client Address */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <label style={styles.fieldLabel}>Client Name/Address</label>
                                    <LeaseRecordInput
                                        style={styles.selectBase}
                                        value={group.clientName1 || ''}
                                        onChange={(e) => handleChange('clientName1', e.target.value)}
                                    />
                                    <LeaseRecordInput
                                        style={styles.selectBase}
                                        value={group.clientName2 || ''}
                                        onChange={(e) => handleChange('clientName2', e.target.value)}
                                    />
                                    <LeaseRecordInput
                                        style={styles.selectBase}
                                        value={group.clientName3 || ''}
                                        onChange={(e) => handleChange('clientName3', e.target.value)}
                                    />
                                    <LeaseRecordInput
                                        style={styles.selectBase}
                                        value={group.clientName4 || ''}
                                        onChange={(e) => handleChange('clientName4', e.target.value)}
                                    />

                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '80px 1fr',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            marginTop: '0.5rem',
                                        }}
                                    >
                                        <label style={styles.fieldLabel}>Landman</label>
                                        <LeaseRecordInput
                                            style={styles.selectBase}
                                            value={group.clientLandman || ''}
                                            onChange={(e) => handleChange('clientLandman', e.target.value)}
                                        />
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 40px 80px', alignItems: 'center', gap: '0.5rem' }}>
                                        <label style={styles.fieldLabel}>City</label>
                                        <LeaseRecordInput
                                            style={styles.selectBase}
                                            value={group.city || ''}
                                            onChange={(e) => handleChange('city', e.target.value)}
                                        />
                                        <LeaseRecordInput
                                            style={styles.selectBase}
                                            value={group.state || ''}
                                            placeholder="St"
                                            onChange={(e) => handleChange('state', e.target.value)}
                                        />
                                        <LeaseRecordInput
                                            style={styles.selectBase}
                                            value={group.zip || ''}
                                            placeholder="Zip"
                                            onChange={(e) => handleChange('zip', e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Right Column: Signature Block */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', alignItems: 'center', gap: '0.5rem' }}>
                                        <label style={styles.fieldLabel}>Salutation</label>
                                        <LeaseRecordInput
                                            style={styles.selectBase}
                                            value={group.salutation || ''}
                                            onChange={(e) => handleChange('salutation', e.target.value)}
                                        />
                                    </div>

                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginTop: '0.5rem' }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={styles.fieldLabel}>Signature</label>
                                            <LeaseRecordInput
                                                style={styles.selectBase}
                                                value={group.signName || ''}
                                                onChange={(e) => handleChange('signName', e.target.value)}
                                            />
                                        </div>
                                        <button
                                            onClick={() => setShowPersonModal(true)}
                                            style={{ ...styles.buttonSecondary, padding: '0.4rem 0.8rem', fontSize: '0.8rem', height: '42px' }}
                                        >
                                            LR Personnel
                                        </button>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', alignItems: 'center', gap: '0.5rem' }}>
                                        <label style={styles.fieldLabel}>Signature Title</label>
                                        <LeaseRecordInput
                                            style={styles.selectBase}
                                            value={group.signTitle || ''}
                                            onChange={(e) => handleChange('signTitle', e.target.value)}
                                        />
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', alignItems: 'center', gap: '0.5rem' }}>
                                        <label style={styles.fieldLabel}>Signature Phone</label>
                                        <LeaseRecordInput
                                            style={styles.selectBase}
                                            value={group.signPhone || ''}
                                            onChange={(e) => handleChange('signPhone', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Letter Text */}
                            <div style={{ marginTop: '1rem' }}>
                                <label style={styles.fieldLabel}>Letter Text</label>
                                <textarea
                                    style={{ ...styles.selectBase, height: '100px', resize: 'vertical', fontFamily: 'inherit' }}
                                    value={group.letterText || ''}
                                    onChange={(e) => handleChange('letterText', e.target.value)}
                                />
                            </div>

                            {/* Bottom Options Grid */}
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: '1rem 2rem',
                                    marginTop: '1.5rem',
                                    padding: '1rem',
                                    backgroundColor: '#E5E7EB',
                                    borderRadius: '6px',
                                    border: '1px solid #D1D5DB',
                                }}
                            >
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '1rem', alignItems: 'center' }}>
                                    <label style={{ ...styles.fieldLabel, textAlign: 'right', marginBottom: 0 }}>Cover Letter?</label>
                                    <select
                                        style={styles.selectBase}
                                        value={group.coverLetter ?? 0}
                                        onChange={(e) => handleChange('coverLetter', Number(e.target.value))}
                                    >
                                        <option value={1}>Yes</option>
                                        <option value={0}>No</option>
                                    </select>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '1rem', alignItems: 'center' }}>
                                    <label style={{ ...styles.fieldLabel, textAlign: 'right', marginBottom: 0 }}>Print Page No.?</label>
                                    <select
                                        style={styles.selectBase}
                                        value={group.printPageNo ?? 0}
                                        onChange={(e) => handleChange('printPageNo', Number(e.target.value))}
                                    >
                                        <option value={1}>Yes</option>
                                        <option value={0}>No</option>
                                    </select>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '1rem', alignItems: 'center' }}>
                                    <label style={{ ...styles.fieldLabel, textAlign: 'right', marginBottom: 0 }}>Net Acres</label>
                                    <LeaseRecordInput
                                        type="number"
                                        style={styles.selectBase}
                                        value={group.netAcres ?? ''}
                                        onChange={(e) => handleChange('netAcres', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '1rem', alignItems: 'center' }}>
                                    <label style={{ ...styles.fieldLabel, textAlign: 'right', marginBottom: 0 }}>Bonus or Option?</label>
                                    <select
                                        style={styles.selectBase}
                                        value={group.bonusOrOption ?? 1}
                                        onChange={(e) => handleChange('bonusOrOption', Number(e.target.value))}
                                    >
                                        <option value={1}>Bonus</option>
                                        <option value={0}>Option to Extend</option>
                                    </select>
                                </div>
                                <div />
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '1rem', alignItems: 'center' }}>
                                    <label style={{ ...styles.fieldLabel, textAlign: 'right', marginBottom: 0, whiteSpace: 'nowrap' }}>
                                        Print Section No on Rec. Fee?
                                    </label>
                                    <select
                                        style={styles.selectBase}
                                        value={group.printSectionNo ?? 0}
                                        onChange={(e) => handleChange('printSectionNo', Number(e.target.value))}
                                    >
                                        <option value={1}>Yes</option>
                                        <option value={0}>No</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div style={styles.modalFooter}>
                    <div style={{ flex: 1, display: 'flex', gap: '0.5rem' }}>
                        {onCreateClick && (
                            <button onClick={onCreateClick} style={styles.buttonPrimary}>
                                Create New
                            </button>
                        )}
                        <button onClick={() => setShowLeasesModal(true)} style={styles.buttonSecondary} disabled={!group?.groupNo}>
                            Leases
                        </button>

                        <button
                            onClick={handlePrintPdf}
                            style={styles.buttonSecondary}
                            disabled={!group?.groupNo || loading || isDownloadingPdf}
                            title="Generate PDF Report"
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {isDownloadingPdf ? (
                                    <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                                ) : (
                                    <BiPrinter />
                                )}
                                {isDownloadingPdf ? 'Generating...' : 'Print'}
                            </div>
                        </button>

                        <button
                            onClick={handleDownloadExcel}
                            style={styles.buttonSecondary}
                            disabled={!group?.groupNo || loading || isDownloadingExcel}
                            title="Generate Excel List"
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {isDownloadingExcel ? (
                                    <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                                ) : (
                                    <BiSpreadsheet />
                                )}
                                {isDownloadingExcel ? 'Generating...' : 'Excel'}
                            </div>
                        </button>
                        <button
                            onClick={handlePrintLprs}
                            style={styles.buttonSecondary}
                            disabled={!group?.groupNo || loading || isDownloadingLprs}
                            title="Generate LPRs for this Invoice Group"
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {isDownloadingLprs ? (
                                    <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                                ) : (
                                    <BiPrinter />
                                )}
                                {isDownloadingLprs ? 'Generating...' : 'Print LPRs'}
                            </div>
                        </button>
                        <button
                            onClick={handleRemove}
                            style={{ ...styles.buttonSecondary, color: '#DC2626', borderColor: '#FCA5A5', backgroundColor: '#FEF2F2' }}
                            disabled={!group?.groupNo || loading}
                        >
                            {loading ? 'Removing...' : 'Remove'}
                        </button>
                    </div>
                    <button onClick={onClose} style={styles.buttonSecondary} disabled={isSaving}>
                        Close
                    </button>
                    {group && (
                        <button onClick={handleSave} style={{ ...styles.buttonPrimary, marginLeft: '0.5rem' }} disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    )}
                </div>
            </div>

            <LRPersonnelModal isOpen={showPersonModal} onClose={() => setShowPersonModal(false)} onSelect={handlePersonSelect} />

            <GroupLeasesModal isOpen={showLeasesModal} onClose={() => setShowLeasesModal(false)} groupNo={group?.groupNo || null} />
        </div>
    );
};

// --- Helpers ---
const Field = ({ label, value }: { label: string; value: string | number | null | undefined }) => (
    <div style={styles.fieldContainer}>
        <label style={styles.fieldLabel}>{label}</label>
        <LeaseRecordInput
            style={{ ...styles.selectBase, backgroundColor: styles.inputDisabled.backgroundColor, color: styles.inputDisabled.color }}
            value={value || ''}
            readOnly={true}
        />
    </div>
);

// --- Styles ---
const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(3px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        width: '90%',
        maxWidth: '1000px',
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
        overflow: 'hidden',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.25rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#111827',
        margin: 0,
    },
    gridBody: {
        maxHeight: '60vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        padding: '0.25rem',
    },
    modalFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        padding: '1rem',
        borderTop: '1px solid #E5E7EB',
        backgroundColor: '#fff',
    },
    buttonPrimary: {
        padding: '0.6rem 1.5rem',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: '#2563EB',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '1rem',
    },
    buttonSecondary: {
        padding: '0.6rem 1.5rem',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '1rem',
    },
    fieldContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.35rem',
    },
    fieldLabel: {
        fontSize: '0.85rem',
        color: '#374151',
        fontWeight: 600,
    },
    selectBase: {
        padding: '0.5rem 0.75rem',
        border: '1px solid #D1D5DB',
        borderRadius: '6px',
        fontSize: '0.9rem',
        color: '#111827',
        backgroundColor: '#fff',
        outline: 'none',
        transition: 'all 0.2s',
        width: '100%',
        boxSizing: 'border-box',
    },
    inputDisabled: {
        backgroundColor: '#F3F4F6',
        color: '#6B7280',
        cursor: 'not-allowed',
    },
    saveError: {
        color: '#B91C1C',
        fontSize: '0.85rem',
        marginRight: '1rem',
        maxWidth: '260px',
    },
    colors: {
        gray50: '#F9FAFB',
        gray100: '#F3F4F6',
        border: '#E5E7EB',
        gray500: '#6B7280',
    } as unknown as React.CSSProperties,
    iconButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#6B7280',
    },
};

export default InvoiceGroupModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/InvoiceGroupModal.tsx